// src/pages/Checkout.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import API from "../api/axios";

const Checkout = ({ user }) => {
  const { cart, clearCart } = useContext(CartContext);
  const navigate = useNavigate();

  // Datos de envío
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [region, setRegion] = useState("");
  const [commune, setCommune] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("Chile");

  // Método de envío y fechas
  const [shippingMethod, setShippingMethod] = useState("chileexpress");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  // Función para calcular el precio con descuento
  const getDiscountedPrice = (product) => {
    return product.discount > 0
      ? product.price * (1 - product.discount / 100)
      : product.price;
  };

  const subtotal = cart.reduce(
    (acc, item) => acc + getDiscountedPrice(item) * item.quantity,
    0
  );

  // Costo de envío: Ejemplo: envío particular cuesta 5000 si está en RM; de lo contrario, Chileexpress cuesta 3000
  const calculateShippingCost = () => {
    if (shippingMethod === "particular") {
      if (
        region.toLowerCase().includes("metropolitana") ||
        commune.toLowerCase().includes("santiago")
      ) {
        return 5000;
      }
      return 0;
    }
    return 3000;
  };

  const shippingCost = calculateShippingCost();
  const total = subtotal + shippingCost;

  // Cargar fechas disponibles cuando se elige envío particular
  useEffect(() => {
    const fetchDates = async () => {
      if (shippingMethod === "particular") {
        try {
          const { data } = await API.get("/shipping/dates");
          setAvailableDates(data);
        } catch (error) {
          console.error("Error al obtener fechas de envío:", error);
        }
      } else {
        setAvailableDates([]);
      }
    };
    fetchDates();
  }, [shippingMethod]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      alert("Tu carrito está vacío.");
      return;
    }
    if (!user) {
      alert("Debes iniciar sesión para continuar.");
      return;
    }
    if (shippingMethod === "particular" && !selectedDate) {
      alert("Por favor selecciona una fecha para el envío particular.");
      return;
    }

    const orderData = {
      products: cart.map((item) => ({
        product: item._id,
        quantity: item.quantity,
      })),
      total,
      shippingDate: shippingMethod === "particular" ? selectedDate : "",
      shippingMethod,
      address,
      region,
      commune,
      postalCode,
      country,
    };

    try {
      const { data } = await API.post("/orders", orderData);
      clearCart();
      alert("Orden creada correctamente.");
      navigate("/order-confirmation");
    } catch (error) {
      console.error("Error al crear la orden:", error);
      alert("Error al crear la orden.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <h1 className="text-3xl font-bold text-center mb-6">Checkout</h1>
      {cart.length === 0 ? (
        <div className="text-center">
          <p>Tu carrito está vacío.</p>
          <a href="/" className="text-blue-600 underline">
            Regresar al inicio
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto bg-white p-6 rounded shadow">
          <h2 className="text-xl font-bold mb-2">Datos de Envío</h2>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <input
              type="text"
              placeholder="Nombre Completo"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="border p-2 rounded w-full"
            />
            <input
              type="tel"
              placeholder="Teléfono"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="border p-2 rounded w-full"
            />
          </div>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Dirección"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              className="border p-2 rounded w-full"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <input
              type="text"
              placeholder="Región"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              required
              className="border p-2 rounded w-full"
            />
            <input
              type="text"
              placeholder="Comuna"
              value={commune}
              onChange={(e) => setCommune(e.target.value)}
              required
              className="border p-2 rounded w-full"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <input
              type="text"
              placeholder="Código Postal"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="border p-2 rounded w-full"
            />
            <input
              type="text"
              placeholder="País"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="border p-2 rounded w-full"
            />
          </div>

          <h2 className="text-xl font-bold mb-2">Método de Envío</h2>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="shippingMethod"
                value="chileexpress"
                checked={shippingMethod === "chileexpress"}
                onChange={(e) => setShippingMethod(e.target.value)}
                className="mr-2"
              />
              Chileexpress
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="shippingMethod"
                value="particular"
                checked={shippingMethod === "particular"}
                onChange={(e) => setShippingMethod(e.target.value)}
                className="mr-2"
              />
              Envío Particular
            </label>
          </div>

          {shippingMethod === "particular" && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">(Solo disponible en la Región Metropolitana / Santiago)</p>
              {availableDates.length > 0 ? (
                <div className="mt-2">
                  <label className="block mb-1 font-semibold">
                    Selecciona la fecha de entrega:
                  </label>
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border p-2 rounded w-full sm:w-1/2"
                  >
                    <option value="">-- Selecciona --</option>
                    {availableDates.map((date) => (
                      <option key={date} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="text-red-500">No hay fechas disponibles para envío particular.</p>
              )}
            </div>
          )}

          <h2 className="text-xl font-bold mb-2">Resumen del Pedido</h2>
          <div className="border rounded p-4 mb-4">
            {cart.map((item, i) => {
              const discountedPrice = getDiscountedPrice(item);
              return (
                <p key={i} className="text-sm text-gray-700 mb-1">
                  {item.title} - x {item.quantity} = $
                  {(discountedPrice * item.quantity).toFixed(2)}
                </p>
              );
            })}
            <p className="mt-2 text-sm text-gray-700">
              Subtotal: ${subtotal.toFixed(2)}
            </p>
            <p className="text-sm text-gray-700">
              Envío: ${shippingCost.toFixed(2)}
            </p>
            <p className="text-lg font-bold mt-2">
              Total: ${total.toFixed(2)}
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Finalizar Compra
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Checkout;
